import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { MessageBubble } from './MessageBubble';
import { AnalyticsMessage } from './AnalyticsMessage';
import { TypingIndicator } from './TypingIndicator';
import { WelcomeMessage } from './WelcomeMessage';
import { ClarificationMessage } from './ClarificationMessage';
import { EventClarificationMessage } from './EventClarificationMessage';
import { useAgents } from '../../hooks/useAgents';
import { useProjectContext } from '../../contexts/ProjectContext';
import { Send, Paperclip, Mic, FolderOpen, AlertCircle } from 'lucide-react';
import { pushGTMEvent, usePageContext } from '../../utils/tracking';

interface Message {
  id: string;
  content: string;
  type: 'user' | 'assistant' | 'clarification';
  timestamp: string;
  visualizations?: any[];
  followUpQuestions?: string[];
  processingTime?: number;
  tokensUsed?: number;
  clarification?: boolean;
  clarificationType?: string;
  suggestions?: string[];
  // Event clarification specific fields
  eventClarification?: boolean;
  events?: Array<{
    eventName: string;
    displayName?: string;
    description?: string;
    example?: string;
    eventCount?: number;
    lastSeen?: string;
  }>;
  originalQuery?: string;
  context?: string;
  confidence?: number;
  queryDetails?: {
    metrics?: string[];
    dimensions?: string[];
    dateRange?: { startDate: string; endDate: string };
    filters?: any[];
    orderBy?: any[];
    limit?: number;
    endpoint?: string;
    cached?: boolean;
    executionTime?: number;
  };
  structuredData?: {
    title?: string;
    summary?: string;
    keyMetrics?: Array<{
      label: string;
      value: string;
      change?: string;
      trend?: 'up' | 'down' | 'stable' | 'unknown';
    }>;
    insights?: string[];
    businessContext?: string[];
    dataTable?: {
      headers: string[];
      rows: string[][];
      title: string;
    };
    queryInfo?: {
      dateRange?: string;
      dataPoints?: number;
      primaryMetrics?: string[];
    };
  };
}

interface ChatInterfaceProps {
  agentType: string;
  conversationId?: string;
  onNewConversation: () => void;
  onConversationCreated?: (newConversationId: string) => void;
  projectId?: string;
  initialQuestion?: string | null;
}

export function ChatInterface({ agentType, conversationId, onNewConversation, onConversationCreated, projectId, initialQuestion }: ChatInterfaceProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  const { chat, useConversation } = useAgents();
  const { pageName } = usePageContext();
  const { selectedProject } = useProjectContext();
  
  // Fetch conversation history if conversationId is provided
  const { data: conversationData, isLoading: isLoadingConversation, error: conversationError } = useConversation(
    conversationId || '',
    { limit: 50 }
  );

  // Clear messages when conversationId changes (including when it becomes undefined for new conversations)
  useEffect(() => {
    console.log('🔄 Conversation ID changed:', {
      conversationId,
      previousMessages: messages.length
    });
    
    // Clear messages when starting a new conversation (conversationId becomes undefined)
    if (!conversationId) {
      console.log('🆕 Starting new conversation, clearing messages');
      setMessages([]);
      setInputValue('');
      // Focus input for new conversation
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [conversationId]);

  // Debug conversation data
  useEffect(() => {
    console.log('🔄 Conversation data changed:', {
      conversationId,
      hasData: !!conversationData,
      isLoading: isLoadingConversation,
      error: conversationError,
      messagesCount: conversationData?.messages?.length || 0
    });
  }, [conversationData, isLoadingConversation, conversationError, conversationId]);

  useEffect(() => {
    if (conversationId && conversationData?.messages) {
      // Format messages for display
      const formattedMessages = conversationData.messages.map((msg: any) => {
        console.log('📝 Formatting message:', {
          id: msg.id,
          type: msg.type,
          hasMetadata: !!msg.metadata,
          metadata: msg.metadata
        });

        
        return {
          id: msg.id,
          content: msg.content,
          type: msg.type === 'user' ? 'user' : 
                msg.metadata?.clarification || msg.metadata?.clarificationType ? 'clarification' : 'assistant',
          timestamp: msg.createdAt,
          visualizations: msg.visualizations,
          followUpQuestions: msg.metadata?.followUpQuestions,
          processingTime: msg.processingTime,
          tokensUsed: msg.tokensUsed,
          queryDetails: msg.metadata?.queryDetails,
          structuredData: msg.metadata?.structuredData,
          // Clarification specific fields
          clarification: msg.metadata?.clarification,
          clarificationType: msg.metadata?.clarificationType,
          suggestions: msg.metadata?.suggestions || [],
          // Event clarification specific fields
          eventClarification: msg.metadata?.clarificationType === 'event_clarification',
          events: msg.metadata?.structuredData?.suggestedEvents?.map((event: any) => ({
            eventName: event.eventName,
            displayName: event.eventName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
            description: event.description,
            example: event.example
          })) || [],
          originalQuery: msg.content
        };
      });
      
      // Smart update: replace with server data when we have new messages
      setMessages(prev => {
        // Filter out temporary messages for comparison
        const nonTempPrev = prev.filter(msg => !msg.id.startsWith('temp-'));
        
        // If server has more messages than our non-temp messages, use server data
        if (formattedMessages.length > nonTempPrev.length) {
          return formattedMessages;
        }
        
        // If server has same number of messages, check if they're different
        if (formattedMessages.length === nonTempPrev.length && formattedMessages.length > 0) {
          const lastServer = formattedMessages[formattedMessages.length - 1];
          const lastLocal = nonTempPrev[nonTempPrev.length - 1];
          
          // If the last message is different, use server data
          if (lastServer.id !== lastLocal.id) {
            return formattedMessages;
          }
        }
        
        // Otherwise, keep existing messages (including temp ones)
        return prev;
      });
    } else if (conversationId && conversationData && !conversationData.messages) {
      // Empty conversation with conversationId
      setMessages([]);
    }
  }, [conversationId, conversationData?.messages]);

  // Enhanced scroll function that can target specific messages
  const scrollToTarget = (targetType: 'bottom' | 'lastAgentMessage' = 'bottom') => {
    if (targetType === 'lastAgentMessage') {
      // Find the last agent message (assistant, clarification, or analytics)
      const lastAgentMessage = [...messages].reverse().find(msg => 
        msg.type === 'assistant' || msg.type === 'clarification'
      );
      
      if (lastAgentMessage) {
        const messageElement = messageRefs.current.get(lastAgentMessage.id);
        if (messageElement) {
          // Scroll to the top of the agent message with some offset
          messageElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
          return;
        }
      }
    }
    
    // Fallback to bottom scroll
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Enhanced scroll effect that determines scroll behavior based on message changes
  useEffect(() => {
    if (messages.length === 0) return;

    // Check if the last message is from an agent (not user)
    const lastMessage = messages[messages.length - 1];
    const isUserMessage = lastMessage?.type === 'user';
    const isTemporaryMessage = lastMessage?.id.startsWith('temp-');
    
    // If it's a user message, temporary message, or loading, scroll to bottom
    if (isUserMessage || isTemporaryMessage || isLoading) {
      scrollToTarget('bottom');
    } else {
      // If it's a real agent message (not temporary), scroll to the top of that message
      // Add a small delay to ensure the message is fully rendered
      setTimeout(() => scrollToTarget('lastAgentMessage'), 150);
    }
  }, [messages, isLoading]);

  useEffect(() => {
    // Focus input when component mounts
    inputRef.current?.focus();
  }, []);

  // Handle onboarding question auto-send
  useEffect(() => {
    // Check for initial question from URL params or sessionStorage (fallback)
    const questionToSend = initialQuestion || sessionStorage.getItem('onboarding_question');
    
    if (questionToSend && !conversationId && messages.length === 0 && !isLoading) {
      // Clear sessionStorage if it was used
      if (sessionStorage.getItem('onboarding_question')) {
        sessionStorage.removeItem('onboarding_question');
      }
      
      // Set the input value and auto-send the question
      setInputValue(questionToSend);
      
      // Auto-send the question after a short delay
      setTimeout(() => {
        if (isLoading) return;
        
        // Safety check for agentType
        if (!agentType || agentType === 'undefined') {
          return;
        }

        const messageContent = questionToSend;
        setInputValue('');
        setIsLoading(true);

        // Add user message optimistically
        const tempUserMessage: Message = {
          id: `temp-user-${Date.now()}`,
          content: messageContent,
          type: 'user',
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, tempUserMessage]);

        // Call the API
        chat({
          agentType,
          message: messageContent,
          conversationId,
          projectId
        }).then(response => {
          if (response.newConversation && response.conversationId) {
            onConversationCreated?.(response.conversationId);
          }
        }).catch(error => {
          console.error('Failed to send initial message:', error);
          setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
          setInputValue(messageContent);
        }).finally(() => {
          setIsLoading(false);
        });
      }, 500);
    }
  }, [conversationId, messages.length, isLoading, agentType, projectId, initialQuestion]);

  const handleSendMessage = async (method: 'button' | 'keyboard' = 'button') => {
    if (!inputValue.trim() || isLoading) return;
    
    // Safety check for agentType
    if (!agentType || agentType === 'undefined') {
      return;
    }

    const messageContent = inputValue;
    
    // Track agent message sent event
    pushGTMEvent({
      elementType: method === 'button' ? 'button' : 'input',
      elementId: `agent-message-send-${pageName}-${agentType}-${method}`,
      elementText: method === 'button' ? 'Send Message' : 'Enter Key Send',
      pageName,
      section: 'chat-interface',
      variant: agentType,
      userAction: method === 'button' ? 'click' : 'submit',
      customProperties: {
        agent_type: agentType,
        message_length: messageContent.length,
        conversation_id: conversationId || 'new',
        project_id: projectId || null,
        send_method: method,
        message_count: messages.filter(m => m.type === 'user').length + 1
      }
    });

    setInputValue('');
    setIsLoading(true);

    // Add user message optimistically (appears immediately)
    const tempUserMessage: Message = {
      id: `temp-user-${Date.now()}`,
      content: messageContent,
      type: 'user',
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      // Call the API to get AI response
      const response = await chat({
        agentType,
        message: messageContent,
        conversationId,
        projectId
      });

      console.log('💬 Chat response received:', {
        newConversation: response.newConversation,
        conversationId: response.conversationId,
        currentConversationId: conversationId
      });

      // If this was a new conversation, update the URL
      if (response.newConversation && response.conversationId) {
        console.log('🔄 Creating new conversation, navigating to:', response.conversationId);
        onConversationCreated?.(response.conversationId);
      }
      
      // The conversation cache will be invalidated automatically and refetched
      // Server response will replace temp message with real data
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove the temporary message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
      // Re-enable input on error
      setInputValue(messageContent);
    } finally {
      setIsLoading(false);
    }
  };



  const handleFollowUpQuestion = (question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  const handleEventsSelect = async (selectedEvents: string[], followUpQuery: string) => {
    // Auto-send the follow-up query with selected events
    if (isLoading) return;
    
    // Safety check for agentType
    if (!agentType || agentType === 'undefined') {
      return;
    }

    // Track event selection and follow-up query
    pushGTMEvent({
      elementType: 'button',
      elementId: `agent-events-select-${pageName}-${agentType}`,
      elementText: 'Select Events and Continue',
      pageName,
      section: 'chat-interface',
      variant: agentType,
      userAction: 'click',
      customProperties: {
        agent_type: agentType,
        selected_events: selectedEvents,
        events_count: selectedEvents.length,
        follow_up_query: followUpQuery,
        conversation_id: conversationId || 'new',
        project_id: projectId || null,
        interaction_type: 'event_clarification_response'
      }
    });

    setInputValue('');
    setIsLoading(true);

    try {
      // Call the API to get AI response
      const response = await chat({
        agentType,
        message: followUpQuery,
        conversationId,
        projectId
      });

      // If this was a new conversation, update the URL
      if (response.newConversation && response.conversationId) {
        onConversationCreated?.(response.conversationId);
      }
      
      // The conversation cache will be invalidated automatically and refetched
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isAnalyticsMessage = (message: Message) => {
    // Detect if this is an analytics response that should use the enhanced component
    return message.type === 'assistant' && 
           agentType === 'data' && 
           (message.queryDetails || 
            message.content.includes('GA4') || 
            message.content.includes('Analytics') ||
            message.content.includes('Performance Metrics') ||
            message.content.includes('Key Insights') ||
            message.content.includes('Business Context'));
  };

  const handleSuggestionSelect = async (suggestion: string) => {
    // Check if the last message was a clarification
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.type === 'clarification') {
      // For clarifications, auto-send the suggestion
      if (isLoading) return;
      
      // Safety check for agentType
      if (!agentType || agentType === 'undefined') {
        return;
      }

      // Track suggestion selection
      pushGTMEvent({
        elementType: 'button',
        elementId: `agent-suggestion-select-${pageName}-${agentType}`,
        elementText: suggestion,
        pageName,
        section: 'chat-interface',
        variant: agentType,
        userAction: 'click',
        customProperties: {
          agent_type: agentType,
          suggestion_text: suggestion,
          conversation_id: conversationId || 'new',
          project_id: projectId || null,
          interaction_type: 'clarification_suggestion_auto_send',
          clarification_type: lastMessage?.clarificationType || 'unknown'
        }
      });

      setInputValue('');
      setIsLoading(true);

      try {
        // Call the API to get AI response
        const response = await chat({
          agentType,
          message: suggestion,
          conversationId,
          projectId
        });

        // If this was a new conversation, update the URL
        if (response.newConversation && response.conversationId) {
          onConversationCreated?.(response.conversationId);
        }
        
        // The conversation cache will be invalidated automatically and refetched
      } catch (error) {
        console.error('Failed to send message:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // For regular follow-up questions, just populate the input
      pushGTMEvent({
        elementType: 'button',
        elementId: `agent-followup-select-${pageName}-${agentType}`,
        elementText: suggestion,
        pageName,
        section: 'chat-interface',
        variant: agentType,
        userAction: 'click',
        customProperties: {
          agent_type: agentType,
          suggestion_text: suggestion,
          conversation_id: conversationId || 'new',
          project_id: projectId || null,
          interaction_type: 'follow_up_question_populate'
        }
      });
      
      handleFollowUpQuestion(suggestion);
    }
  };

  if (isLoadingConversation && conversationId) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Loading conversation...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 mobile-chat-container">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-2 py-3 sm:px-3 sm:py-4 space-y-3 sm:space-y-4 bg-gray-50 dark:bg-gray-900 mobile-scroll mobile-chat-messages">
        {messages.length === 0 ? (
          <WelcomeMessage agentType={agentType} onQuestionSelect={handleFollowUpQuestion} />
        ) : (
          messages.map((message) => {
            console.log('🎨 Rendering message:', {
              id: message.id,
              type: message.type,
              eventClarification: message.eventClarification,
              eventsCount: message.events?.length,
              clarification: message.clarification,
              clarificationType: message.clarificationType
            });
            
            // Create a ref callback to store message element references
            const setMessageRef = (el: HTMLDivElement | null) => {
              if (el) {
                messageRefs.current.set(message.id, el);
              } else {
                messageRefs.current.delete(message.id);
              }
            };
            
            if (message.type === 'clarification') {
              // Check if this is an event clarification
              if (message.eventClarification && message.events && message.events.length > 0) {
                console.log('🎯 Rendering EventClarificationMessage with events:', message.events);
                return (
                  <div key={message.id} ref={setMessageRef}>
                    <EventClarificationMessage
                      question={message.content}
                      events={message.events}
                      originalQuery={message.originalQuery || ''}
                      onEventsSelect={handleEventsSelect}
                    />
                  </div>
                );
              } else {
                console.log('🎯 Rendering ClarificationMessage with suggestions:', message.suggestions);
                return (
                  <div key={message.id} ref={setMessageRef}>
                    <ClarificationMessage
                      question={message.content}
                      suggestions={message.suggestions}
                      onSuggestionSelect={handleSuggestionSelect}
                    />
                  </div>
                );
              }
            }
            
            if (isAnalyticsMessage(message)) {
              return (
                <div key={message.id} ref={setMessageRef}>
                  <AnalyticsMessage
                    message={message}
                    onFollowUpQuestion={handleFollowUpQuestion}
                  />
                </div>
              );
            }
            
            return (
              <div key={message.id} ref={setMessageRef}>
                <MessageBubble
                  message={message}
                  onFollowUpQuestion={handleFollowUpQuestion}
                />
              </div>
            );
          })
        )}
        
        {isLoading && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Enhanced Mobile Design */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0 safe-area-padding-bottom">
        <div className="px-3 py-3 sm:px-4 sm:py-4">
          {/* Project Indicator - Mobile Optimized */}
          {selectedProject ? (
            <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <div className="flex items-center space-x-2 min-w-0">
                <FolderOpen className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-600 dark:text-gray-300 flex-shrink-0">
                  Analyzing:
                </span>
                <Badge variant="outline" className="text-sm truncate max-w-32 sm:max-w-none">
                  {selectedProject.name}
                </Badge>
                {selectedProject.ga4PropertyId && (
                  <Badge variant="secondary" className="text-xs">
                    GA4
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            <div className="mb-3 flex items-start space-x-2 p-2.5 sm:p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
              <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-yellow-700 dark:text-yellow-300 leading-relaxed">
                No project selected. Please select a project from the header to analyze data.
              </span>
            </div>
          )}
          
          {/* Input Container - Mobile First Design */}
          <div className="flex items-end space-x-2">
            <div className="flex-1">
              <div className="relative">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage('keyboard');
                    }
                  }}
                  placeholder={selectedProject 
                    ? `Ask about ${selectedProject.name}'s analytics...` 
                    : `Ask the ${agentType} agent anything...`
                  }
                  className="w-full min-h-[44px] max-h-32 p-3 pr-12 sm:pr-16 text-mobile-base text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 mobile-rounded resize-none mobile-input-focus transition-all duration-200"
                  disabled={isLoading}
                  rows={1}
                  style={{ 
                    height: 'auto',
                    minHeight: '44px'
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = Math.min(target.scrollHeight, 128) + 'px';
                  }}
                />
                {/* Action Buttons Inside Input */}
                <div className="absolute right-2 bottom-2 flex items-center space-x-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    disabled={isLoading}
                  >
                    <Paperclip className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg hidden sm:flex"
                    disabled={isLoading}
                  >
                    <Mic className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Send Button - Enhanced Mobile Design */}
            <Button
              onClick={() => handleSendMessage('button')}
              disabled={!inputValue.trim() || isLoading}
              className="bg-gradient-to-r from-blue-600 to-blue-900 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 h-11 w-11 sm:h-10 sm:w-auto sm:px-4 mobile-rounded flex-shrink-0 shadow-lg hover:shadow-xl mobile-touch-feedback transition-all duration-200"
              size="sm"
            >
              <Send className="w-5 h-5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline sm:ml-2">Send</span>
            </Button>
          </div>
          
          {/* Helper Text - Mobile Optimized */}
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="hidden sm:inline">Press Enter to send, Shift+Enter for new line</span>
            <span className="sm:hidden text-xs">Tap Send or press Enter</span>
            <span className="text-xs font-medium">{inputValue.length}/4000</span>
          </div>
        </div>
      </div>
    </div>
  );
}